import React, { useState, useEffect } from 'react';
import { 
  Wallet, RefreshCw, Settings, LogOut, 
  Smartphone, ShieldCheck, Activity, Loader2, TrendingUp, Clock, HelpCircle, ExternalLink
} from 'lucide-react';

import { initializeApp } from "firebase/app";
import { 
  getFirestore, doc, onSnapshot, updateDoc, setDoc 
} from "firebase/firestore";
import { 
  getAuth, signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged 
} from "firebase/auth";

// ⚠️ KENDİ CONFIG BİLGİLERİNİ BURAYA YAPIŞTIRMAYI UNUTMA
const firebaseConfig = {
  apiKey: "AIzaSyAC4J7Zucwx4BZKmz7dhQtq8LalgR-zE4A",
  authDomain: "cs2-market-bot.firebaseapp.com",
  projectId: "cs2-market-bot",
  storageBucket: "cs2-market-bot.firebasestorage.app",
  messagingSenderId: "1043706527020",
  appId: "1:1043706527020:web:1de04c61edd8b4caca0089"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const provider = new GoogleAuthProvider();

export default function CS2SimpleDashboard() {
  const [user, setUser] = useState(null);
  const [userSettings, setUserSettings] = useState({ 
    telegramId: '', steamId: '', total_value: 0, last_inventory_sync: null, request_report: false
  });
  const [activeTab, setActiveTab] = useState('dashboard'); 

  // Helper to format date
  const formatDate = (val) => {
    if (!val) return 'Never';
    if (typeof val === 'object' && val.seconds) return new Date(val.seconds * 1000).toLocaleString();
    return val;
  };

  // Auth & Data Listener
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const unsubDoc = onSnapshot(doc(db, "users", currentUser.uid), (docSnap) => {
          if (docSnap.exists()) setUserSettings(docSnap.data());
        });
        return () => unsubDoc();
      }
    });
    return () => unsubscribeAuth();
  }, []);

  const handleLogin = async () => { try { await signInWithPopup(auth, provider); } catch (e) { alert(e.message); } };
  const handleLogout = () => signOut(auth);

  const handleSaveSettings = async () => {
    if (!user) return;
    try {
      await setDoc(doc(db, "users", user.uid), {
        telegramId: userSettings.telegramId,
        steamId: userSettings.steamId,
        email: user.email
      }, { merge: true });
      alert("Settings Saved!");
    } catch (e) { alert(e.message); }
  };

  const handleSync = async () => {
    if (!userSettings.steamId || !userSettings.telegramId) return alert("Please set IDs in Settings first!");
    try {
      await updateDoc(doc(db, "users", user.uid), { request_report: true });
      alert("Bot started! You will receive a message on Telegram shortly.");
    } catch (e) { alert(e.message); }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 text-center">
        <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-blue-500/30 shadow-xl">
          <Wallet size={40} className="text-white" />
        </div>
        <h1 className="text-4xl font-bold text-white mb-2">CS2 Value Tracker</h1>
        <p className="text-slate-400 mb-8 max-w-md">Track your inventory value automatically. Receive daily updates on Telegram.</p>
        <button onClick={handleLogin} className="bg-white hover:bg-slate-100 text-slate-900 font-bold py-3 px-8 rounded-xl transition-all flex items-center gap-2">
           Sign in with Google
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans flex flex-col md:flex-row">
      {/* SIDEBAR */}
      <aside className="w-full md:w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Activity className="text-blue-500" /> ValueTracker
          </h1>
          <p className="text-xs text-slate-500 mt-2 truncate">{user.email}</p>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          <button onClick={() => setActiveTab('dashboard')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'dashboard' ? 'bg-blue-600/10 text-blue-400' : 'hover:bg-slate-800'}`}>
            <Wallet size={20} /> Dashboard
          </button>
          <button onClick={() => setActiveTab('settings')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'settings' ? 'bg-blue-600/10 text-blue-400' : 'hover:bg-slate-800'}`}>
            <Settings size={20} /> Settings
          </button>
        </nav>
        <div className="p-4 border-t border-slate-800">
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-500 hover:text-red-400 text-sm w-full"><LogOut size={16} /> Logout</button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 p-8 overflow-y-auto">
        {activeTab === 'dashboard' && (
          <div className="max-w-4xl mx-auto">
            <header className="flex justify-between items-center mb-10">
              <div>
                <h2 className="text-3xl font-bold text-white">Overview</h2>
                <p className="text-slate-400">Your daily inventory statistics.</p>
              </div>
              <button 
                onClick={handleSync} 
                disabled={userSettings.request_report}
                className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-blue-600/20"
              >
                {userSettings.request_report ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                {userSettings.request_report ? 'Processing...' : 'Calculate Now'}
              </button>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* TOTAL VALUE CARD */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 p-8 rounded-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-6 opacity-10"><Wallet size={100} /></div>
                <h3 className="text-slate-400 font-medium uppercase tracking-wider mb-2">Total Inventory Value</h3>
                <div className="text-5xl font-bold text-white mb-4 flex items-baseline gap-2">
                  ${userSettings.total_value?.toFixed(2) || '0.00'}
                  <span className="text-lg text-slate-500 font-normal">USD</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400 bg-slate-950/50 inline-flex px-3 py-1 rounded-lg">
                  <Clock size={14} /> Last Updated: {formatDate(userSettings.last_inventory_sync)}
                </div>
              </div>

              {/* INFO CARD */}
              <div className="bg-slate-900 border border-slate-700 p-8 rounded-2xl flex flex-col justify-center">
                <h3 className="text-white font-bold text-xl mb-4 flex items-center gap-2">
                  <TrendingUp className="text-green-500" /> Automatic Reporting
                </h3>
                <p className="text-slate-400 mb-4">
                  Your inventory is automatically scanned every day at <b>12:00 PM</b>.
                </p>
                <ul className="space-y-2 text-sm text-slate-500">
                  <li className="flex items-center gap-2">✓ Daily Value Report via Telegram</li>
                  <li className="flex items-center gap-2">✓ Percentage Change Calculation</li>
                  <li className="flex items-center gap-2">✓ Safe & Secure (Official Steam API)</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Account Settings</h2>
            
            <div className="space-y-6">
              <div className="bg-slate-900 border border-slate-700 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Smartphone className="text-blue-500"/> Telegram Setup</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Chat ID</label>
                    <input 
                      type="text" 
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-blue-500 outline-none transition-colors"
                      placeholder="e.g. 123456789"
                      value={userSettings.telegramId || ''}
                      onChange={(e) => setUserSettings({...userSettings, telegramId: e.target.value})}
                    />
                  </div>
                  <p className="text-xs text-slate-500 bg-slate-950/50 p-3 rounded border border-slate-800/50">
                    1. Find <b>@userinfobot</b> on Telegram.<br/>
                    2. Start the bot to get your numeric ID.<br/>
                    3. Start a chat with YOUR bot so it can message you.
                  </p>
                </div>
              </div>

              <div className="bg-slate-900 border border-slate-700 p-6 rounded-xl">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><ShieldCheck className="text-green-500"/> Steam Setup</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Steam ID64</label>
                    <input 
                      type="text" 
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-blue-500 outline-none transition-colors"
                      placeholder="e.g. 76561198..."
                      value={userSettings.steamId || ''}
                      onChange={(e) => setUserSettings({...userSettings, steamId: e.target.value})}
                    />
                  </div>
                  
                  {/* --- YENİ EKLENEN KISIM: YARDIM KUTUSU --- */}
                  <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4 text-sm text-slate-400 space-y-2">
                    <div className="flex items-center gap-2 text-white font-bold text-xs uppercase tracking-wider">
                      <HelpCircle size={14} /> How to find your ID?
                    </div>
                    <ul className="list-disc list-inside space-y-1 ml-1 text-xs">
                      <li>Go to <a href="https://steamid.io" target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline flex items-center gap-1 inline-flex">steamid.io <ExternalLink size={10}/></a></li>
                      <li>Paste your profile link and copy "steamID64".</li>
                      <li>Make sure your <b>Inventory Privacy</b> is set to <b>Public</b> on Steam.</li>
                    </ul>
                  </div>
                  {/* ----------------------------------------- */}

                </div>
              </div>

              <button 
                onClick={handleSaveSettings} 
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-blue-600/20"
              >
                Save Configuration
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}